import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, Users, Trash2, Edit3, ChevronLeft, Activity,
  UserCheck, UserX, Eye, Lock, Save, X, RefreshCw, Filter, MessageCircle, AlertTriangle, Settings, UserPlus
} from "lucide-react";
import {
  adminGetUsersAPI, adminEditUserAPI, adminDeleteUserAPI,
  adminGetUserLogsAPI, adminGetStatsAPI
} from "../services/api";

interface UserInfo {
  username: string;
  role: string;
  created_at: string;
  last_login: string | null;
  disabled: boolean;
  chat_count: number;
}

interface AdminStats {
  total_users: number;
  active_today: number;
  total_chats: number;
}

interface UsageLog {
  action: string;
  detail: string;
  timestamp: string;
}

type LogFilter = "all" | "chat" | "chat_error" | "create_character" | "edit_character" | "delete_character" | "update_settings" | "login" | "admin_login";

const LOG_ACTION_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  login:              { label: "登录",       color: "bg-green-500/20 text-green-400",     icon: "🟢" },
  admin_login:        { label: "管理员登录", color: "bg-purple-500/20 text-purple-400",   icon: "🟣" },
  chat:               { label: "对话",       color: "bg-blue-500/20 text-blue-400",       icon: "💬" },
  chat_error:         { label: "对话错误",   color: "bg-red-500/20 text-red-400",         icon: "❌" },
  create_character:   { label: "创建角色",   color: "bg-rose-500/20 text-rose-400",       icon: "✨" },
  edit_character:     { label: "编辑角色",   color: "bg-amber-500/20 text-amber-400",     icon: "✏️" },
  delete_character:   { label: "删除角色",   color: "bg-red-500/20 text-red-400",         icon: "🗑️" },
  update_settings:    { label: "修改设置",   color: "bg-cyan-500/20 text-cyan-400",       icon: "⚙️" },
};

export function AdminPanel({ onBack }: { onBack: () => void }) {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [stats, setStats] = useState<AdminStats>({ total_users: 0, active_today: 0, total_chats: 0 });
  const [editUser, setEditUser] = useState<UserInfo | null>(null);
  const [editPassword, setEditPassword] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editDisabled, setEditDisabled] = useState(false);
  const [logsUser, setLogsUser] = useState<string | null>(null);
  const [logs, setLogs] = useState<UsageLog[]>([]);
  const [logFilter, setLogFilter] = useState<LogFilter>("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function loadData() {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        adminGetUsersAPI(),
        adminGetStatsAPI(),
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  async function handleEditUser() {
    if (!editUser) return;
    const data: { password?: string; role?: string; disabled?: boolean } = {};
    if (editPassword) data.password = editPassword;
    if (editRole !== editUser.role) data.role = editRole;
    if (editDisabled !== editUser.disabled) data.disabled = editDisabled;
    try {
      await adminEditUserAPI(editUser.username, data);
      setSuccess(`用户 ${editUser.username} 已更新`);
      setEditUser(null);
      setEditPassword("");
      loadData();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function handleDeleteUser(username: string) {
    if (!confirm(`确定删除用户 ${username}？此操作不可撤销。`)) return;
    try {
      await adminDeleteUserAPI(username);
      setSuccess(`用户 ${username} 已删除`);
      loadData();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function handleShowLogs(username: string) {
    try {
      const res = await adminGetUserLogsAPI(username);
      setLogs(res.data);
      setLogsUser(username);
      setLogFilter("all");
    } catch (err: any) {
      setError(err.message);
    }
  }

  function formatDate(iso: string | null) {
    if (!iso) return "-";
    return new Date(iso).toLocaleString("zh-CN", {
      month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit"
    });
  }

  const filteredLogs = logFilter === "all"
    ? logs
    : logs.filter(l => l.action === logFilter);

  return (
    <div className="h-full overflow-y-auto bg-slate-950 p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={onBack} className="p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              管理后台
            </h1>
            <p className="text-sm text-slate-400">用户管理与系统监控</p>
          </div>
          <button onClick={loadData} disabled={loading} className="ml-auto p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Success / Error */}
        <AnimatePresence>
          {success && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-2 mb-4 text-green-400 text-sm">{success}</motion.div>
          )}
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 mb-4 text-red-400 text-sm">{error}</motion.div>
          )}
        </AnimatePresence>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: "总用户数", value: stats.total_users, icon: Users, color: "from-blue-500/20 to-blue-600/20", iconColor: "text-blue-400" },
            { label: "今日活跃", value: stats.active_today, icon: Activity, color: "from-green-500/20 to-green-600/20", iconColor: "text-green-400" },
            { label: "总对话数", value: stats.total_chats, icon: MessageCircle, color: "from-purple-500/20 to-purple-600/20", iconColor: "text-purple-400" },
          ].map((s) => (
            <div key={s.label} className={`bg-gradient-to-br ${s.color} border border-slate-800 rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-2">
                <s.icon className={`w-4 h-4 ${s.iconColor}`} />
                <span className="text-xs text-slate-400">{s.label}</span>
              </div>
              <p className="text-2xl font-bold text-white">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Users Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800">
            <h2 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Users className="w-4 h-4" />
              用户列表 ({users.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 text-xs border-b border-slate-800">
                  <th className="px-4 py-3 text-left">用户名</th>
                  <th className="px-4 py-3 text-left">角色</th>
                  <th className="px-4 py-3 text-left">状态</th>
                  <th className="px-4 py-3 text-left">注册时间</th>
                  <th className="px-4 py-3 text-left">最后登录</th>
                  <th className="px-4 py-3 text-left">对话数</th>
                  <th className="px-4 py-3 text-center">操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.username} className="border-b border-slate-800/50 hover:bg-slate-800/50">
                    <td className="px-4 py-3 text-white font-medium">{u.username}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                        u.role === "admin" ? "bg-purple-500/20 text-purple-400" : "bg-slate-700 text-slate-300"
                      }`}>
                        {u.role === "admin" ? "管理员" : "用户"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.disabled
                        ? <span className="text-red-400 flex items-center gap-1"><UserX className="w-3 h-3" />禁用</span>
                        : <span className="text-green-400 flex items-center gap-1"><UserCheck className="w-3 h-3" />正常</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-slate-400">{formatDate(u.last_login)}</td>
                    <td className="px-4 py-3 text-slate-400">{u.chat_count}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleShowLogs(u.username)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-blue-400" title="查看日志">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => {
                          setEditUser(u); setEditPassword(""); setEditRole(u.role); setEditDisabled(u.disabled);
                        }} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-amber-400" title="编辑">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {u.username !== "admin" && (
                          <button onClick={() => handleDeleteUser(u.username)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-red-400" title="删除">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      <AnimatePresence>
        {editUser && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setEditUser(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-white flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-amber-400" />
                  编辑用户: {editUser.username}
                </h3>
                <button onClick={() => setEditUser(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">新密码（留空不修改）</label>
                  <input type="password" value={editPassword} onChange={(e) => setEditPassword(e.target.value)}
                    placeholder="输入新密码" className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50" />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">角色</label>
                  <select value={editRole} onChange={(e) => setEditRole(e.target.value)}
                    disabled={editUser.username === "admin"}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500/50">
                    <option value="user">用户</option>
                    <option value="admin">管理员</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-slate-400">状态</label>
                  <button onClick={() => setEditDisabled(!editDisabled)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      editDisabled ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-green-500/20 text-green-400 border border-green-500/30"
                    }`}>
                    {editDisabled ? "已禁用" : "正常"}
                  </button>
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setEditUser(null)} className="flex-1 py-3 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition-colors">
                    取消
                  </button>
                  <button onClick={handleEditUser} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-purple-500 text-white font-medium hover:shadow-lg hover:shadow-purple-500/20 transition-all flex items-center justify-center gap-2">
                    <Save className="w-4 h-4" />
                    保存
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logs Modal */}
      <AnimatePresence>
        {logsUser && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setLogsUser(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-400" />
                  操作日志: {logsUser}
                  <span className="text-xs text-slate-500 ml-1">({logs.length} 条)</span>
                </h3>
                <button onClick={() => setLogsUser(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Log Filter */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Filter className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                {([
                  { key: "all" as LogFilter, label: "全部" },
                  { key: "chat" as LogFilter, label: "对话" },
                  { key: "chat_error" as LogFilter, label: "错误" },
                  { key: "create_character" as LogFilter, label: "创建角色" },
                  { key: "edit_character" as LogFilter, label: "编辑角色" },
                  { key: "delete_character" as LogFilter, label: "删除角色" },
                  { key: "update_settings" as LogFilter, label: "设置" },
                  { key: "login" as LogFilter, label: "登录" },
                ]).map(f => (
                  <button
                    key={f.key}
                    onClick={() => setLogFilter(f.key)}
                    className={`px-2 py-0.5 rounded text-[10px] transition-colors ${
                      logFilter === f.key
                        ? "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                        : "bg-slate-800 text-slate-500 hover:text-slate-300 border border-transparent"
                    }`}
                  >
                    {f.label}
                    {f.key !== "all" && (
                      <span className="ml-1 text-slate-600">
                        ({logs.filter(l => l.action === f.key).length})
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto space-y-2">
                {filteredLogs.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">暂无记录</p>
                ) : filteredLogs.map((log, i) => {
                  const actionInfo = LOG_ACTION_LABELS[log.action] || { label: log.action, color: "bg-slate-700 text-slate-300", icon: "📌" };
                  return (
                    <div key={i} className="bg-slate-800 rounded-lg px-3 py-2 flex items-center gap-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${actionInfo.color}`}>
                        {actionInfo.icon} {actionInfo.label}
                      </span>
                      <span className="text-slate-400 text-xs flex-1 truncate">{log.detail}</span>
                      <span className="text-slate-500 text-xs whitespace-nowrap">{formatDate(log.timestamp)}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
